import { SpreadsheetColumnsInvestmentsService } from './../../../../@core/utils/spreadsheet-columns-investments.service';
import { YearsService } from './../../../../@core/utils/years.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { NbDialogService, NbCardModule, NbAlertModule, NbButtonModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { combineLatest, of, Subscription } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { I18nService } from '../../../../@core/utils/i18n.service';
import * as R from 'ramda';
import { KPI } from '../../../../@core/api';
import {
  ChartFiltersService,
  ChartsService,
  InitiativesService,
  InvestmentScenariiService,
  PerimeterEnergyTrajectoryService,
  PerimeterPathwayService,
  PerimetersService,
  ScopeService,
  SpreadsheetColumnsInitiativesService,
} from '../../../../@core/utils';
import { LayoutService } from '../../../../@core/utils';
import { INITIATIVE_FILTER_KEY_PREFIX, INVEST_FILTER_KEY_PREFIX } from '../../../../@core/utils/constants';
import { ExcelExplanationsDialogComponent, SheetDialogComponent } from '../../../../@theme/components';
import { idMapToArray } from '../../../../@core/utils/scope.service';
import { EmissionFactorService } from '../../../../@core/utils/emission-factor.service';
import { HighchartsChartModule } from 'highcharts-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgClass } from '@angular/common';
import { ConsumptionTypeButtonComponent } from '../../consumption-type-button/consumption-type-button.component';
import { BaseTrajectoryChartComponent } from '../base-trajectory-chart/base-trajectory-chart.component';

@Component({
  selector: 'app-energy-trajectory-chart',
  styleUrls: ['../base-trajectory-chart/base-trajectory-chart.component.scss'],
  templateUrl: '../base-trajectory-chart/base-trajectory-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NbCardModule,
    NgClass,
    
    FontAwesomeModule,
    HighchartsChartModule,
    NbAlertModule,
    NbButtonModule,
    TranslateModule,
    ConsumptionTypeButtonComponent,
  ],
})
export class EnergyTrajectoryChartComponent
  extends BaseTrajectoryChartComponent
  implements OnInit, OnChanges, OnDestroy {

  @Input() public sitesNumber: number = null;
  @Input() public isSideView: boolean = false;
  @Input() public customStartYear: number = null;
  @Input() public useMainScenario: boolean = false;
  @Input() public perimeters: KPI.Perimeter[] = [];
  @Input() public forceScenarioInTitle: boolean = false;
  @Input() public forcedScenarii: KPI.InvestmentScenario[];
  @Input() public selectedBuildingIds: number[] = [];
  @Input() public isSiteObjectiveEligible: boolean = true;

  private scenarii: KPI.InvestmentScenario[];
  private chartTitle: string;
  private budgetOrRatioFilter: KPI.BudgetOrRatioFilter;
  private selectedPerimeterIds: number[] = [];
  private plannedInvestmentStatuses: KPI.InvestmentStatus[];
  private subscriptions: Subscription[] = [];
  public chosenProvider: string;
  private pathwayStartYear = new Date().getFullYear() - 3;
  private yearsRange: number[] = R.range(this.pathwayStartYear, 2051);
  private allTrajectoryPoints: {scenario: KPI.InvestmentScenario, trajectoryPoints: KPI.EnergyTrajectoryPoint[]}[]

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    protected router: Router,
    protected pathwayService: PerimeterPathwayService,
    private cdRef: ChangeDetectorRef,
    private i18nService: I18nService,
    private yearsService: YearsService,
    private scopeService: ScopeService,
    private translate: TranslateService,
    private chartsService: ChartsService,
    private perimetersService: PerimetersService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
    private initiativesService: InitiativesService,
    private chartFiltersService: ChartFiltersService,
    private emissionFactorService: EmissionFactorService,
    private investmentScenariiService: InvestmentScenariiService,
    private spreadsheetColumnsInvestmentsService: SpreadsheetColumnsInvestmentsService,
    private spreadsheetColumnsInitiativesService: SpreadsheetColumnsInitiativesService,
  ) {
    super(dialogService, layoutService, router, pathwayService);
  }

  public ngOnInit(): void {
    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.noConsumptionSentence = this.translate.instant("No consumption for the perimeter");
    if (changes['isSiteObjectiveEligible'] && !changes['isSiteObjectiveEligible'].currentValue && this.providerId === -1) {
      this.emissionFactorService.setSelectedProvider('energy', 0);
    }
    this.update();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  protected update(): void {
    this.subscriptions.push(
      combineLatest([
        this.yearsService.latestYears$,
        this.perimetersService.perimeters$,
        this.investmentScenariiService.selectedScenarii$.pipe(
          map((scenarii) => scenarii.filter((s) => !!s)),
        ),
        this.investmentScenariiService.mainScenario$,

      ]).pipe(
        switchMap(([[startYear, endYear], perimeters, selectedScenarii, mainScenario]) => {
          this.scenarii = this.useMainScenario
            ? [mainScenario]
            : this.forcedScenarii
              ? this.forcedScenarii
              : selectedScenarii;
          if (!this.perimeters.length) {
            this.perimeters = perimeters;
          }
          const buildingIds = this.selectedBuildingIds?.length
            ? this.selectedBuildingIds
            : PerimetersService.getBuildingsIds(this.perimeters);
          const energyData$ = combineLatest(
            this.scenarii.map((scenario) => {
              const options = {
                buildings: buildingIds.join(','),
                scenario: scenario.id,
              };
              if (this.customStartYear) {
                options['startYear'] = this.customStartYear;
              }
              return this.chartsService.getEnergyTrajectoryData(options).pipe(
                map((energyData) => ({ scenario, trajectoryPoints: energyData.trajectory_points }))
              );
            }),
          );

          return combineLatest([
            energyData$,
            of([startYear, endYear]),
            this.chartFiltersService.filter$('budgetOrRatio'),
            this.scopeService.investmentStatuses$.pipe(idMapToArray()),
            this.emissionFactorService.getSelectedProvider$('energy'),
            this.pathwayService.requestPathway$(this.perimeters, this.yearsRange),
            this.pathwayService.getSelectedPathway(),
            this.initiativesService.initiativeEnergyTypes$,
            this.perimeterEnergyTrajectoryService.selectedTrajectoryPerimeterIds$,
          ])
        }),
      ).subscribe(([trajectories, [startYear, endYear], budgetOrRatioFilter,
        investmentStatuses, provider, pathway, selectedToUPathway, energyTypes, selectedPerimeterIds]) => {
        this.selectedPerimeterIds = selectedPerimeterIds;

        this.selectedToUPathway = selectedToUPathway;

        this.loading = true;
        this.providerId = provider.id
        this.chosenProvider = provider.name;
        const years = this.customStartYear ? R.range(this.customStartYear, endYear + 1) : R.range(startYear, endYear + 1);
        const i18n = this.i18nService;
        this.allTrajectoryPoints = trajectories

        const objectiveData = [2030, 2040, 2050].map((year) => {
          if (this.providerId === null || this.providerId === 0) {
            return [];
          }
          if (this.providerId !== -1 && selectedToUPathway?.consumption) {
            return this.getObjectiveDataByToU(years.length, selectedToUPathway.consumption[year]);
          } else if(pathway?.site_objectives) {
            return this.getObjectiveDataBySite(years.length, pathway.site_objectives, year, 'energy', this.selectedPerimeterIds);
          } else {
            return [];
          }
        });

        const [objectiveData2030, objectiveData2040, objectiveData2050] = objectiveData;

        this.plannedInvestmentStatuses = investmentStatuses.filter((status) =>
          !status.hypothesis && status.show_in_charts,
        );

        const trajectoryPoints = trajectories[0].trajectoryPoints;

        const relevantInvestmentStatuses = investmentStatuses.filter((status) =>
          (trajectoryPoints.some((point) => point.investments_by_year_and_status.some((s) => s.id === status.id)))
          && status.show_in_charts,
        ).sort((a, _b) => a.hypothesis ? 1 : -1);
        this.budgetOrRatioFilter = budgetOrRatioFilter as number;
        const baseColumnSerie = {
          budget: (
            budgetOrRatioFilter === KPI.BudgetOrRatioFilter.CAPEX_AMOUNT
          ),
          column: true,
          trajectory: false,
          type: 'column',
          yAxis: 'y-axis-2',
          zIndex: 1,
          opacity: 0.9,
          dataLabels: {
            enabled: this.scenarii?.length > 1,
            formatter: function () {
              if (this.y) {
                return i18n.formatCurrency(this.y);
              }
            }
          }
        }

        this.chartTitle = this.translate.instant(_('pages.energy.overview.energy-trajectory-chart.title_1'));
        let columnSerieName: string = '';

        columnSerieName = this.translate.instant(_('Planned invest'));

        const objectiveSerieTemplate = {
          dashStyle: 'dash',
          lineWidth: 1.5,
          marker: {
            enabled: false,
          },
          dataLabels: {
            enabled: false
          },
          column: false,
          trajectory: false,
          type: 'line',
          yAxis: 'y-axis-1',
          zIndex: 3,
        };

        const series: any[] = [
          // kWh Trajectory by scenario
          ...this.scenarii.map((scenario, i) => ({
            name: this.translate.instant('Trajectory') + ' ' + scenario.name,
            data: trajectories[i]?.trajectoryPoints.filter((trajectory) => years.includes(trajectory.year)).map((trajectory) =>
              Math.round(trajectory.energy_consumption / trajectory.area) || null,
            ),
            lineWidth: 1,
            marker: {
              symbol: 'square',
              radius: 5
            },
            yAxis: 'y-axis-1',
            zIndex: 2,
            trajectory: true,
            column: false,
            color: scenario.color,
            energyTypes,
            scenario: scenario
          })),
          // Planned investments OR savings by Scenario
          ...this.scenarii.map((scenario, i) => ({
            name: `${columnSerieName} ${scenario.name}`,
            data: trajectories[i]?.trajectoryPoints.filter((trajectory) => years.includes(trajectory.year)).map((trajectory) => {
              const investment_budget: number = this.getPlannedStatuses(
                trajectory.investments_by_year_and_status
              ).reduce((acc, s) => acc += s.investment_price, 0);
              return investment_budget;
            }),
            scenario: scenario.id,
            ...baseColumnSerie,
            visible: trajectories.length > 1 && budgetOrRatioFilter !== KPI.BudgetOrRatioFilter.TRAJECTORY_ONLY,
            showInLegend: trajectories.length > 1 && budgetOrRatioFilter !== KPI.BudgetOrRatioFilter.TRAJECTORY_ONLY,
            color: scenario.color,
          })),
          // Spliting investments by status - CASE OF ONE SCENARIO
          ...relevantInvestmentStatuses.map((status, i) => ({
            name: !i ? this.translate.instant(_('invest_at_status'), { name: status.name }) : status.name,
            data: trajectoryPoints.filter((trajectory) => years.includes(trajectory.year)).map((trajectory) => {
              return trajectory.investments_by_year_and_status.find((s) => s.id === status.id)?.investment_price || 0;
            }),
            color: status.color,
            scenario: this.scenarii[0].id,
            investmentStatus: status.id,
            chartData: trajectoryPoints,
            altName: status.name,
            ...baseColumnSerie,
            visible: trajectories.length === 1
              && budgetOrRatioFilter === KPI.BudgetOrRatioFilter.CAPEX_AMOUNT,
            showInLegend: trajectories.length === 1
              && budgetOrRatioFilter === KPI.BudgetOrRatioFilter.CAPEX_AMOUNT,
          })),
          // KWH OBJECTIVE 2030
          {
            ...objectiveSerieTemplate,
            name: this.translate.instant('Objective')
              + ' 2030',
            data: objectiveData2030,
            lineWidth: 3,
            color: '#00b0f0',
            showInLegend: !!objectiveData2030?.[0],
            visible: !!objectiveData2030?.[0],
          },
          // KWH OBJECTIVE 2040
          {
            ...objectiveSerieTemplate,
            name: this.translate.instant('Objective') + ' 2040',
            data: objectiveData2040,
            lineWidth: 3,
            color: '#ffc000',
            showInLegend: !!objectiveData2040[0],
            visible: !!objectiveData2040[0],
          },
          // KWH OBJECTIVE 2050
          {
            ...objectiveSerieTemplate,
            name: this.translate.instant('Objective') + ' 2050',
            data: objectiveData2050,
            lineWidth: 3,
            color: '#ff9b9b',
            showInLegend: !!objectiveData2050[0],
            visible: !!objectiveData2050[0],
          },
        ];

        const categoryNames = trajectoryPoints.filter((trajectory) =>
          years.includes(trajectory.year)
        ).map((trajectory) => ({
          name: trajectory.year,
          sitesNb: trajectory.sites_count,
        }));

        const noSerieHasAnyData = series.every((serie) => serie.data.every((d) => d === null || !d || isNaN(d)));
        // We need at least 1 energy declaration per selected site or else we don't show the chart
        // Skip this check when specific buildings are selected, as sites_count is per-site not per-building
        const allSitesHaveTrajectories = this.selectedBuildingIds?.length > 0
          || trajectories[0].trajectoryPoints[0].sites_count === this.perimeters.length;

        if (noSerieHasAnyData || !allSitesHaveTrajectories) {
          this.displayNoData = true;
        } else {
          this.displayNoData = false;
          // Update chart options
          this.options = {
            ...this.getOptions(),
            // @ts-ignore
            xAxis: {
              ...this.getOptions().xAxis,
              // @ts-ignore
              categories: categoryNames,
              tickWidth: 0,
              labels: {
                style: {
                  fontSize: '13px',
                },
              },
            },
            // @ts-ignore
            series: series as Highcharts.SeriesColumnOptions[],
          };

          setTimeout(() => {
            this.reflow();
            this.cdRef.detectChanges();
          }, 50);
        }
        this.loading = false;

        this.cdRef.detectChanges();
      }),
    );
  }

  protected getOptions(): Highcharts.Options {
    const i18n = this.i18nService;
    const translate = this.translate;
    const budgetOrRatioFilter = this.budgetOrRatioFilter;
    const allTrajectoryPoints = this.allTrajectoryPoints

    return {
      ...super.getOptions(),
      title: {
        style: super.getOptions().title.style,
        align: 'left',
        text: this.chartTitle,
      },
      legend: {
        align: 'center',
        verticalAlign: 'top',
        itemStyle: {
          fontSize: '0.9375rem',
          fontWeight: 'normal',
        },
        itemHiddenStyle: super.getOptions().legend.itemHiddenStyle,
        symbolHeight: 11,
        symbolRadius: 0,
        itemMarginBottom: 8,
      },
      tooltip: {
        enabled: true,
        split: true,
        shared: true,
        useHTML: true,
        formatter: function () {
          const tooltipText: string[] = [''].concat(
            this.points
              ? this.points.map((point) => {
                let text: string
                let diffPercent: number
                let formatPercent: string
                const investmentStatus = point.series.userOptions['investmentStatus'];
                const energyTypes = point.series.userOptions['energyTypes'];
                const altName = point.series.userOptions['altName'];
                let chartData;
                if (investmentStatus || energyTypes) {
                  chartData = point.series.userOptions['chartData'];
                }
                const year = point.point.category['name'];

                if (point.series.userOptions["trajectory"]) {
                  const serieScenario = point.series.userOptions['scenario']

                  const contribution = allTrajectoryPoints.find(traj => traj.scenario.id === serieScenario.id).trajectoryPoints.find((trajectory) => trajectory.year === year).contributions;
                  const energy_map: { [energyTypeId: number]: number } = contribution;
                  const emissionTotal = Object.values(energy_map).reduce((acc, emission) => acc + emission, 0);

                  const emissionsPercentStr = Object.keys(energy_map).filter((key) =>
                    energy_map[key],
                  ).map((key) => {
                    const energy = energyTypes.find((energyType) => energyType.id === Number(key));
                    const energyPercent = Math.min(
                      100,
                      Math.round(energy_map[key] / emissionTotal * 100)
                    );
                    return `<b>${energy.name}</b> ${energyPercent > 0 ? energyPercent : 0}%`;
                  }).join(' <b>|</b> ');

                  text = '<b> '
                    + point.series.name
                    + ': </b>'
                    + new Intl.NumberFormat(i18n.getCulture(), { style: 'decimal' }).format(point.y)
                    + ' '
                    + translate.instant(_('pages.energy.kwh_ef_m2_year'))
                    + `<br/><span style="color: #c71f1f;">${emissionsPercentStr}</span>`;
                  if (diffPercent > 0) {
                    text += ' : '
                      + formatPercent
                      + translate.instant(_('pages.energy.vsObjective'));
                  }
                  else if (diffPercent === 0) {
                    text += ' : <span style="color:green">'
                      + translate.instant(_('pages.decision.analysis.cards.objective-reached'))
                      + '</span>';
                  }
                  else if (diffPercent < 0) {
                    text += ' : <span style="color:green">'
                      + translate.instant(_('pages.decision.analysis.cards.objective-exceeded'))
                      + '</span>';
                  }
                } else if (point.series.userOptions["column"] && point.y) {
                  text = '<b> '
                    + point.series.name
                    + ': </b>';
                    if (investmentStatus && chartData) {
                      const nbInv = chartData.find((trajectory) => trajectory.year === year
                        && trajectory.investments_by_year_and_status.some((s) => s.id === investmentStatus)
                      ).investments_by_year_and_status.find((s) => s.id === investmentStatus).number_of_investment || 0;
                      return translate.instant(
                        _('{nbInv} invest on status « {status} » for {budget}'),
                        {
                          nbInv,
                          status: altName,
                          budget: i18n.formatCurrency(point.y)
                        });
                    } else {
                      return text += i18n.formatCurrency(point.y);
                    }
                } else {
                  return '';
                }
                return text;
              })
              : []
          );
          return tooltipText;
        },
      },
      plotOptions: {
        column: {
          stacking: this.scenarii?.length === 1 ? 'normal' : undefined,
        },
        spline: {
          dataLabels: {
            enabled: false,
          },
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: this.getColumnClickHandler(),
            },
          },
        }
      },
      yAxis: [{
        id: 'y-axis-1',
        gridLineWidth: 1,
        minorGridLineWidth: 0,
        min: 0,
        labels: {
          useHTML: true,
          style: {
            fontSize: '12px',
            fontWeight: 'normal',
          },
          enabled: true,
          formatter: function () {
            return new Intl.NumberFormat(i18n.getCulture(), {
              style: 'decimal',
              // @ts-ignore
            }).format(this.value) + ' ' + translate.instant(_('pages.energy.kwh_ef_m2_year'));
          },
        },
        title: {
          text: null,
        },
      },
      {
        id: 'y-axis-2',
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 'normal',
          },
          enabled: false,
        },
        title: {
          text: null,
        },
        stackLabels: {
          style: {
            fontWeight: 'normal',
          },
          enabled: this.scenarii?.length === 1
            && budgetOrRatioFilter === KPI.BudgetOrRatioFilter.CAPEX_AMOUNT,
          formatter: function () {
            if (this.total) {
              return i18n.formatCurrency(this.total);
            }
          },
        },
        plotLines: [{
          color: '#9d9d9d',
          width: 1,
          value: 0,
          zIndex: 999,
        }]
      }],
      xAxis: {
        crosshair: true,
        labels: {
          style: {
            fontSize: '12px',
            color: '#091902',
          },
        },
      },
      series: [],
    };
  }

  protected getChartOptions(): Highcharts.ChartOptions {
    return {
      ...super.getChartOptions(),
      type: 'spline',
      alignTicks: false,
    };
  }

  public excelMessageHandler() {
    this.dialogService.open(ExcelExplanationsDialogComponent);
  }

  private getPlannedStatuses(trajectoryStatuses: KPI.TrajectoryInvestmentByStatus[]): KPI.TrajectoryInvestmentByStatus[] {
    return trajectoryStatuses.filter((status) => this.plannedInvestmentStatuses.map((s) => s.id).includes(status.id));
  }

  private getColumnClickHandler(): (
    event: Highcharts.SeriesClickEventObject,
  ) => void {

    const router = this.router;
    const isSideView = this.isSideView;
    const dialogService = this.dialogService;
    const scenariiService = this.investmentScenariiService;
    const investColumnsService = this.spreadsheetColumnsInvestmentsService;
    const initiativeColumnsService = this.spreadsheetColumnsInitiativesService;

    return function (event: Highcharts.SeriesClickEventObject) {

      const year = event.point.category['name'];
      const scenario = event.point.series.userOptions['scenario'] || null;
      const energyType = event.point.series.userOptions['energyType'];
      const investmentStatus = event.point.series.userOptions['investmentStatus'];
      const isBudgetColumnClicked: boolean = event.point.series.userOptions['budget'];

      if (isBudgetColumnClicked) {

        combineLatest([
          investColumnsService.plannedStatusFilter$,
          scenariiService.scenarii$,
          scenariiService.mainScenario$
        ]).pipe(first()).subscribe(([statuses, scenarii, mainScenario]) => {

          const selectedScenario = scenarii.find((s) => s.id === scenario) || mainScenario;
          
          let queryParams: any = {
            [`${INVEST_FILTER_KEY_PREFIX}year_scenario_${selectedScenario.id}`]: year,
          };

          if (investmentStatus) {
            queryParams[`${INVEST_FILTER_KEY_PREFIX}status_scenario_${selectedScenario.id}`] = `${investmentStatus}`;
          } else {
            queryParams[`${INVEST_FILTER_KEY_PREFIX}status_scenario_${selectedScenario.id}`] = statuses.join(',');
          }

          queryParams[`${INVEST_FILTER_KEY_PREFIX}has_initiatives`] = true;

          setTimeout(() => {
            router.navigate(['single-pages', 'investments'], {
              queryParams,
              queryParamsHandling: 'merge',
            });
          }, 300);
        });

      } else {

        initiativeColumnsService.plannedStatusFilter$.pipe(first()).subscribe((statuses) => {

          if (scenario?.id) {
            if (isSideView) {
              const context: any = {
                autoFocus: false,
                initiativesListScenario: scenario,
                initiativesList: true,
                initiativesListFilters: {
                  [`savings_start_year_scenario_${scenario.id}`]: [year],
                  [`initiative_status_scenario_${scenario.id}`]: statuses.map(String)
                },
              };

              dialogService.open(
                SheetDialogComponent,
                {
                  context,
                }
              );

            } else {

              const queryParams: any = {
                [`${INITIATIVE_FILTER_KEY_PREFIX}savings_start_year_scenario_${scenario.id}`]: year,
                [`${INITIATIVE_FILTER_KEY_PREFIX}initiative_status_scenario_${scenario.id}`]: statuses.join(','),
              };

              if (energyType) {
                queryParams[`${INITIATIVE_FILTER_KEY_PREFIX}energy_type`] = energyType;
              }

              setTimeout(() => {
                const newRoute = ['energy'];
                router.navigate(newRoute, { queryParams });
              }, 300);
            }
          }
        });
      }
    };
  }

}
